import React, { PropsWithChildren } from 'react';
import { useForm } from 'react-hook-form';
import cn from 'clsx';
import Button from '@components-clean/core/button';
import { TextAreaController } from '@components-clean/core/text-area';
import { TextAreaDropdownController } from '@components-clean/core/text-area-dropdown';
import { TextBox, TextBoxController } from '@components-clean/core/text-box';
import { NumericTextBox, NumericTextBoxController } from '@components-clean/core/text-box-numeric';

interface IProps {
    isDisabled: boolean;
}

interface SectionProps extends PropsWithChildren {
    title: string;
    className?: string;
}
const Section = ({ title, className, children }: SectionProps) => {
    return (
        <div className={cn('k-d-flex k-d-flex-col k-gap-4', className)}>
            <h2 className="k-m-0">{title}</h2>
            {children}
        </div>
    );
};

const Inputs = ({ isDisabled }: IProps) => {
    interface IForm {
        numericSolidInput?: number;
        numericSolidInputRequired: number;
        numericSolidInputSpecificRange: number;
        numericSolidInputReadOnly: number;
        numericFlatInput: number;
        numericFlatInputRequired: number;
        numericFlatInputSpecificRange: number;
        numericFlatInputReadOnly: number;
        textSolidInput: string;
        textSolidInputRequired: string;
        textSolidInputReadOnly: string;
        textSolidInputCopy: string;
        textSolidInputReadOnlyCopy: string;
        textSolidInputReadOnlyInactive: string;
        textFlatInput: string;
        textFlatInputRequired: string;
        textFlatInputReadOnly: string;
        textAreaSolid: string;
        textAreaFlat: string;
        textAreaDropdown: string;
    }

    const { control, handleSubmit: handleSubmitHookForm } = useForm<IForm>({
        mode: 'all',
    });

    return (
        <div className="k-pb-8" style={{ minWidth: 150, maxWidth: 350 }}>
            <form
                // eslint-disable-next-line no-alert -- Allow alert on Dummy Pages.
                onSubmit={handleSubmitHookForm((values) => alert(JSON.stringify(values, null, 2)))}
                noValidate
                className="k-d-flex-col k-gap-8"
            >
                <Section title="Numeric Inputs">
                    <NumericTextBoxController
                        control={control}
                        name="numericSolidInput"
                        data-testid="numeric-solid-input"
                        label="Numeric Solid - (Optional)"
                        disabled={isDisabled}
                    />

                    <NumericTextBoxController
                        control={control}
                        name="numericSolidInputRequired"
                        data-testid="numeric-solid-input-required"
                        label="Numeric Solid - (Required | Min 10)"
                        disabled={isDisabled}
                        min={10}
                        required
                    />

                    <NumericTextBoxController
                        control={control}
                        name="numericSolidInputSpecificRange"
                        data-testid="numeric-solid-input-specific-range"
                        label="Numeric Solid - (Required | Specific Range) - Only absolute"
                        disabled={isDisabled}
                        required
                        format="#"
                        min={10}
                        max={99}
                    />

                    <NumericTextBox
                        name="numericSolidInputDisabled"
                        data-testid="numeric-solid-input-disabled"
                        label="Numeric Solid - (Disabled)"
                        disabled
                    />

                    <NumericTextBox
                        name="numericSolidInputReadOnly"
                        data-testid="numeric-solid-input-read-only"
                        label="Numeric Solid - (Read-Only)"
                        disabled={isDisabled}
                        readOnly
                        value={123}
                    />

                    <NumericTextBoxController
                        control={control}
                        name="numericFlatInput"
                        fillMode="flat"
                        data-testid="numeric-flat-input"
                        label="Numeric Flat - (Optional)"
                        disabled={isDisabled}
                    />

                    <NumericTextBoxController
                        control={control}
                        name="numericFlatInputRequired"
                        fillMode="flat"
                        data-testid="numeric-flat-input-required"
                        label="Numeric Flat Field - (Required)"
                        disabled={isDisabled}
                        required
                    />

                    <NumericTextBoxController
                        control={control}
                        name="numericFlatInputSpecificRange"
                        fillMode="flat"
                        data-testid="numeric-flat-input-specific-range"
                        label="Numeric Flat - (Required | Specific Range)"
                        disabled={isDisabled}
                        required
                        min={10}
                        max={99}
                    />

                    <NumericTextBox
                        fillMode="flat"
                        name="numericFlatInputDisabled"
                        data-testid="numeric-flat-input-disabled"
                        label="Numeric Flat - (Disabled)"
                        disabled
                    />

                    <NumericTextBox
                        fillMode="flat"
                        name="numericFlatInputReadOnly"
                        data-testid="numeric-flat-input-read-only"
                        label="Numeric Flat - (Read-Only)"
                        disabled={isDisabled}
                        readOnly
                        value={123}
                    />
                </Section>

                <Section title="Text Inputs">
                    <TextBoxController
                        control={control}
                        name="textSolidInput"
                        data-testid="text-solid-input"
                        label="Text Solid - (Optional)"
                        disabled={isDisabled}
                    />
                    <TextBoxController
                        control={control}
                        name="textSolidInputRequired"
                        data-testid="text-input-flat-required"
                        label="Text Solid - (Required | Limit)"
                        disabled={isDisabled}
                        maxLength={10}
                        required
                    />
                    <TextBox
                        name="textSolidInputDisabled"
                        data-testid="text-input-solid-disabled"
                        label="Text Solid - (Disabled)"
                        disabled
                    />
                    <TextBox
                        name="textSolidInputReadOnly"
                        data-testid="text-input-flat-required"
                        label="Text Solid - (Read-Only)"
                        disabled={isDisabled}
                        readOnly
                        value="This is a read-only value"
                    />
                    <TextBoxController
                        control={control}
                        name="textSolidInputCopy"
                        data-testid="text-solid-input-copy"
                        label="Text Solid with Copy - (Required)"
                        disabled={isDisabled}
                        copyBtn
                        required
                    />
                    <TextBox
                        name="textSolidInputReadOnlyCopy"
                        data-testid="text-input-redonly-copy"
                        label="Text Solid with Copy - (Read-Only)"
                        disabled={isDisabled}
                        readOnly
                        value="This is a read-only value"
                        copyBtn
                    />
                    <TextBox
                        name="textSolidInputReadOnlyInactive"
                        data-testid="text-input-redonly-inactive"
                        label="Text Solid with Inactive value - (Read-Only)"
                        disabled={isDisabled}
                        readOnly
                        inactiveValue
                        value="This is an inactive read-only value"
                    />
                    <TextBoxController
                        control={control}
                        name="textFlatInput"
                        data-testid="text-input-flat-optional"
                        label="Text Flat - (Optional)"
                        fillMode="flat"
                        disabled={isDisabled}
                    />
                    <TextBoxController
                        control={control}
                        name="textFlatInputRequired"
                        data-testid="text-input-flat-required"
                        label="Text Flat - (Required | Limit)"
                        fillMode="flat"
                        disabled={isDisabled}
                        maxLength={10}
                        required
                    />
                    <TextBox
                        fillMode="flat"
                        name="textFlatInputDisabled"
                        data-testid="text-input-flat-disabled"
                        label="Text Flat - (Disabled)"
                        disabled
                    />
                    <TextBox
                        fillMode="flat"
                        name="textFlatInputReadOnly"
                        data-testid="text-input-flat-required"
                        label="Text Flat - (Read-Only)"
                        disabled={isDisabled}
                        readOnly
                        value="This is a read-only value"
                    />
                    <TextAreaController
                        control={control}
                        name="textAreaSolid"
                        data-testid="text-area-solid-required"
                        label="Text Area Solid - (Required | Limit)"
                        fillMode="solid"
                        disabled={isDisabled}
                        maxLength={30}
                        required
                        hintText="Hint Text"
                    />
                    <TextAreaController
                        control={control}
                        name="textAreaFlat"
                        data-testid="text-area-flat-required"
                        label="Text Area Flat - (Required | Limit)"
                        fillMode="flat"
                        disabled={isDisabled}
                        maxLength={30}
                        required
                        hintText="Hint Text"
                    />
                    <TextAreaDropdownController
                        control={control}
                        name="textAreaDropdown"
                        data-testid="text-area-dropdown"
                        label="Text Area Dropdown"
                        fillMode="outline"
                        maxLength={30}
                        disabled={isDisabled}
                        required
                        rows={4}
                    />
                </Section>

                <Button data-testid="submit" primary disabled={isDisabled}>
                    Submit
                </Button>
            </form>
        </div>
    );
};

export default Inputs;
